/**
 * Site Errors — triage for everything the telemetry pipeline captured for this
 * project: browser errors from the storefront, plus (on the platform bucket)
 * admin-SPA, backend and plugin failures.
 *
 * Follows the mandatory admin data standard (CLAUDE.md §12): reads through
 * TanStack Query + <DataState>, keys from `qk`, errors surfaced via
 * `apiErrorMessage`. No hand-rolled useEffect fetching.
 */
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Bug, CheckCircle2, EyeOff, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { apiErrorMessage } from '@/lib/conflict';
import { DataState } from '@/components/common/DataState';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface ErrorGroup {
  id: number;
  project_id: string;
  surface: string;
  fingerprint: string;
  kind: string;
  title: string;
  culprit: string | null;
  first_seen: string;
  last_seen: string;
  event_count: number;
  session_count: number;
  status: string;
  release: string | null;
}

interface Stats {
  newGroups: number;
  activeGroups: number;
  events: number;
  totalEvents: number;
}

const STATUS_STYLES: Record<string, string> = {
  open: 'bg-red-100 text-red-800',
  investigating: 'bg-amber-100 text-amber-800',
  fixed: 'bg-green-100 text-green-800',
  already_fixed: 'bg-green-100 text-green-800',
  resolved: 'bg-green-100 text-green-800', // legacy rows, pre-migration-013
  ignored: 'bg-gray-100 text-gray-700',
};

function timeAgo(iso: string): string {
  const then = new Date(iso.replace(' ', 'T') + (iso.endsWith('Z') ? '' : 'Z')).getTime();
  const mins = Math.max(0, Math.round((Date.now() - then) / 60_000));
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1_440) return `${Math.round(mins / 60)}h ago`;
  return `${Math.round(mins / 1_440)}d ago`;
}

export default function ErrorsPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const queryClient = useQueryClient();
  // 'fixed' is a family filter server-side: it also matches 'already_fixed'
  // and legacy 'resolved' rows.
  const [status, setStatus] = useState<'open' | 'investigating' | 'fixed' | 'ignored' | 'all'>(
    'open'
  );

  const base = `/projects/${projectId}/telemetry`;

  const groupsQuery = useQuery({
    queryKey: qk.telemetryGroups(projectId!, status),
    queryFn: async () => {
      const res = await api.get(`${base}?status=${status}`);
      return (res.data?.data ?? []) as ErrorGroup[];
    },
    enabled: Boolean(projectId),
  });

  const statsQuery = useQuery({
    queryKey: qk.telemetryStats(projectId!),
    queryFn: async () => {
      const res = await api.get(`${base}/stats`);
      return (res.data?.data ?? null) as Stats | null;
    },
    enabled: Boolean(projectId),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: qk.telemetry(projectId!) });
  };

  const setStatusMutation = useMutation({
    mutationFn: async (vars: { fingerprint: string; status: string }) =>
      api.patch(`${base}/${vars.fingerprint}`, { status: vars.status }),
    onSuccess: (_d, vars) => {
      invalidate();
      toast.success(`Marked ${vars.status}`);
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Failed to update status')),
  });

  /**
   * Retention is manual by design — nothing deletes telemetry on a schedule, so
   * this button is the only way rows go away. Confirmed in the UI because it is
   * irreversible.
   */
  const pruneMutation = useMutation({
    mutationFn: async (olderThanDays: number) =>
      api.post(`${base}/prune`, { olderThanDays, dropGroups: false }),
    onSuccess: (res) => {
      invalidate();
      const n = res.data?.data?.events ?? 0;
      toast.success(`Deleted ${n} stored occurrence${n === 1 ? '' : 's'}`);
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Prune failed')),
  });

  const handlePrune = () => {
    if (
      !window.confirm(
        'Delete stored error occurrences older than 30 days for this project?\n\n' +
          'Grouped error summaries (counts, first/last seen) are kept — only the raw ' +
          'per-occurrence rows are removed. This cannot be undone.'
      )
    ) {
      return;
    }
    pruneMutation.mutate(30);
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <Bug className="h-6 w-6" /> Site Errors
          </h1>
          <p className="text-muted-foreground text-sm">
            Captured automatically from every page of this site — no setup per site.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => invalidate()}>
            <RefreshCw className="mr-2 h-4 w-4" /> Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrune}
            disabled={pruneMutation.isPending}
          >
            <Trash2 className="mr-2 h-4 w-4" /> Prune &gt; 30 days
          </Button>
        </div>
      </div>

      <DataState query={statsQuery} hideRefreshingHint>
        {(stats) =>
          stats ? (
            <div className="grid gap-4 sm:grid-cols-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-muted-foreground text-xs font-medium">
                    New (24h)
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-2xl font-semibold">{stats.newGroups}</CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-muted-foreground text-xs font-medium">
                    Open problems
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-2xl font-semibold">{stats.activeGroups}</CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-muted-foreground text-xs font-medium">
                    Events (24h)
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-2xl font-semibold">{stats.events}</CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-muted-foreground text-xs font-medium">
                    Stored occurrences
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-2xl font-semibold">{stats.totalEvents}</CardContent>
              </Card>
            </div>
          ) : null
        }
      </DataState>

      <div className="flex gap-2">
        {(['open', 'investigating', 'fixed', 'ignored', 'all'] as const).map((s) => (
          <Button
            key={s}
            size="sm"
            variant={status === s ? 'default' : 'outline'}
            onClick={() => setStatus(s)}
          >
            {s[0].toUpperCase() + s.slice(1)}
          </Button>
        ))}
      </div>

      <DataState query={groupsQuery}>
        {(groups) =>
          groups.length === 0 ? (
            <Card>
              <CardContent className="text-muted-foreground flex flex-col items-center gap-2 py-12 text-sm">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
                No {status === 'all' ? '' : status} errors recorded.
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Error</TableHead>
                      <TableHead className="w-[110px]">Surface</TableHead>
                      <TableHead className="w-[90px]">Count</TableHead>
                      <TableHead className="w-[110px]">Last seen</TableHead>
                      <TableHead className="w-[100px]">Status</TableHead>
                      <TableHead className="w-[150px]" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {groups.map((g) => (
                      <TableRow key={g.fingerprint}>
                        <TableCell>
                          <div className="flex items-start gap-2">
                            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
                            <div className="min-w-0">
                              <p className="truncate font-medium" title={g.title}>
                                {g.title}
                              </p>
                              {g.culprit && (
                                <p
                                  className="text-muted-foreground truncate font-mono text-xs"
                                  title={g.culprit}
                                >
                                  {g.culprit}
                                </p>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{g.surface}</Badge>
                        </TableCell>
                        <TableCell>
                          <span className="font-medium">{g.event_count}</span>
                          <span className="text-muted-foreground text-xs">
                            {' '}
                            / {g.session_count} sess
                          </span>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-xs">
                          {timeAgo(g.last_seen)}
                        </TableCell>
                        <TableCell>
                          <span
                            className={`rounded px-2 py-0.5 text-xs ${STATUS_STYLES[g.status] ?? ''}`}
                          >
                            {g.status}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Mark fixed"
                              onClick={() =>
                                setStatusMutation.mutate({
                                  fingerprint: g.fingerprint,
                                  status: 'fixed',
                                })
                              }
                            >
                              <CheckCircle2 className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Ignore — stops Slack alerts for this error"
                              onClick={() =>
                                setStatusMutation.mutate({
                                  fingerprint: g.fingerprint,
                                  status: 'ignored',
                                })
                              }
                            >
                              <EyeOff className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )
        }
      </DataState>
    </div>
  );
}
